import Text from 'components/Text/Text';
import React, { useRef } from 'react';
import { Form, Select } from 'antd';
import SelectDropdownReloader from 'components/SelectDropdownReloader/SelectDropdownReloader';
import { Rule } from 'antd/lib/form';
import { SelectProps, DefaultOptionType } from 'antd/lib/select';
import { ISelectOption } from 'components/Select/types';
import { TTextTranslationNamespaces } from 'types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './FormSelect.module.scss';

const { Option } = Select;

type TDefaultSelectProps = Omit<SelectProps, 'options'>;

interface IFormSelect extends TDefaultSelectProps {
	name: string;
	label: string;
	options?: ISelectOption[];
	loading?: boolean;
	fetchError?: boolean;
	placeholder?: string;
	className?: string;
	labelClassName?: string;
	containerClassName?: string;
	formItemClassName?: string;
	validationRules?: Rule[];
	withSearch?: boolean;
	keepSearchWithDropdownRender?: boolean;
	counterValue?: number;
	testId?: string;
	removeIcon?: React.ReactNode;
	translationNameSpace?: TTextTranslationNamespaces;
	onRefetch?: () => void;
	onChange?: (value: any) => void;
}

const FormSelect: React.FC<IFormSelect> = ({
	name,
	label,
	options = [],
	loading,
	fetchError,
	placeholder = 'selectPlaceholder',
	className,
	labelClassName,
	containerClassName,
	formItemClassName,
	validationRules,
	withSearch = true,
	keepSearchWithDropdownRender = false,
	counterValue,
	testId,
	disabled,
	removeIcon = (
		<svg className={style.iconClose}>
			<use xlinkHref={`${breadcrumbs}#closeSelectItem`} data-test="closeSelectItem" />
		</svg>
	),
	translationNameSpace,
	mode,
	dropdownRender,
	onRefetch,
	onChange,
	...restProps
}) => {
	const parentNodeRef = useRef<null | HTMLSpanElement>(null);
	const { t } = useTranslation('form');

	const selectContainerClasses = classNames(style.container, containerClassName);
	const selectClasses = classNames(style.select, { [style.withoutInnerItem]: !!counterValue }, className);
	const labelClasses = classNames(style.label, labelClassName);
	const nameClasses = classNames(style.name, { [style.disabledName]: disabled });
	const selectedCounterClasses = classNames(style.selectedCounter, {
		[style.disabledSelectedCounter]: disabled,
	});
	const optionClasses = (disabled: boolean) => classNames(style.listItem, { [style.disabledItem]: disabled });

	const isRequired = validationRules?.some((rule) => {
		if (typeof rule === 'object') {
			return rule?.required;
		}
		return false;
	});

	const isSearchEnabled = keepSearchWithDropdownRender ? withSearch : !dropdownRender && withSearch;

	const handleRefetch = () => {
		onRefetch?.();
	};

	const handleSearch = (inputValue: string, option: DefaultOptionType | undefined) => {
		if (option?.children && Array.isArray(option?.children)) {
			return String(option.children[1].props.translationKey).toLowerCase().includes(inputValue.toLowerCase());
		}
		return false;
	};

	return (
		<div className={selectContainerClasses} data-test={testId}>
			<div className={style.labelWrapper}>
				<Text
					className={labelClasses}
					translationNameSpace="form"
					translationKey={label}
					size="xs"
					theme="neutralLight"
				/>
				{!!isRequired && <Text translationKey="*" size="xs" theme="neutralLight" display="inline" />}
			</div>
			{!!counterValue && (
				<Text
					className={selectedCounterClasses}
					translationNameSpace="form"
					translationKey="selectedItemsCounter"
					translationOptions={{ counterValue }}
					size="xs"
				/>
			)}
			<span ref={parentNodeRef} style={{ position: 'relative' }} />
			<Form.Item name={name} rules={validationRules} className={formItemClassName}>
				<Select
					className={selectClasses}
					data-test={testId + 'Dropdown'}
					onChange={onChange}
					loading={loading}
					showArrow={true}
					allowClear={mode !== 'multiple' && true}
					disabled={disabled}
					notFoundContent={fetchError ? <SelectDropdownReloader onReload={handleRefetch} /> : undefined}
					dropdownRender={dropdownRender}
					showSearch={isSearchEnabled}
					filterOption={isSearchEnabled ? handleSearch : undefined}
					listItemHeight={options.length}
					getPopupContainer={() => parentNodeRef.current as HTMLSpanElement}
					placeholder={!counterValue ? t(placeholder) : undefined}
					removeIcon={!counterValue ? removeIcon : undefined}
					mode={mode}
					{...restProps}>
					{options.length ? (
						<>
							<Option
								className={style.disabledItem}
								value="empty"
								disabled={true}
								data-test="value-empty">
								<Text
									translationNameSpace="form"
									translationKey="selectAnyItemPlaceholder"
									asFragment={true}
								/>
							</Option>
							{options.map((el) => {
								return (
									<Option
										className={optionClasses(!!el.disabled)}
										value={el.id}
										key={el.id}
										disabled={el.disabled}
										data-test={`value-${el.name}`}>
										{el.icon}
										<Text
											className={nameClasses}
											translationKey={el.name}
											translationNameSpace={translationNameSpace}
											withTooltip={true}
											size="xs"
											weight="medium"
										/>
										{el.additionalContent}
									</Option>
								);
							})}
						</>
					) : null}
				</Select>
			</Form.Item>
		</div>
	);
};

export default React.memo(FormSelect);
